import { isAnswerComplete } from "@pathwright/blocks-core/src/blocks/question/type/completion"
import {
  BLOCK_INPUT_STATES,
  useBlockCompletion,
  useBlocksConfig
} from "@pathwright/blocks-engine"
import { QuillEditor } from "@pathwright/blocks/src/shared"
import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import TextArea from "@pathwright/ui/src/components/form/form-textarea/TextArea"
import { TOOLBAR_CONFIG_TYPES } from "@pathwright/ui/src/components/quill/toolbar/QuillToolbar"
import { memo, useCallback, useEffect, useRef } from "react"
import styled from "styled-components"
import { isFirefox } from "../../../utils"
import { getTextFromHTML } from "../utils"

const ParagraphQuestionViewWrapper = styled.div`
  .ParagraphQuestionView__rich-text-editor .ql-editor {
    min-height: 75px;
  }

  // Placeholder should match the positioning of the cursor
  .ql-editor.ql-blank:before {
    left: 5px;
  }

  @media screen and (max-width: 480px) {
    .blocks-paragraph__textarea div:first-of-type {
      align-items: stretch;
    }
  }

  .ParagraphQuestionView__rich-text-editor .ql-editor,
  .ParagraphQuestionView__rich-text-editor .ql-editor > p,
  .ParagraphQuestionView__rich-text-editor .ql-editor.ql-blank:before,
  .blocks-paragraph__textarea {
    ${sansSerifStyles({ size: 18 })}
  }
`

const StyledInput = styled.div`
  min-height: 87px;
  width: 100%;
  padding: 5px;
  margin-top: 5px;
  border: 1px solid #292e35;
  border-radius: 4px;
  background-color: transparent;

  /* Solves Safari printing bug */
  @media print {
    height: 0;
  }

  .ql-container {
    line-height: 25px;
    color: #292e35;
  }
`

const MIN_ANSWER_LENGTH = 1

const ParagraphRichTextEditor = memo(
  ({ html, onChange, preventChanges }) => (
    <StyledInput className="ParagraphQuestionView__rich-text-editor">
      <QuillEditor
        toolbar={TOOLBAR_CONFIG_TYPES.SIMPLE}
        alwaysEditable
        html={html}
        onChange={onChange}
        readOnly={preventChanges}
        contentType={null}
        // Uncontrolled editing is smoother for learners, and skipping sync-driven
        // rerenders in that mode prevents the page from jumping back to this
        // block when a background save completes.
        controlled={!!preventChanges}
      />
    </StyledInput>
  ),
  (prevProps, nextProps) => {
    if (!prevProps.preventChanges && !nextProps.preventChanges) {
      return true
    }

    return (
      prevProps.html === nextProps.html &&
      prevProps.onChange === nextProps.onChange &&
      prevProps.preventChanges === nextProps.preventChanges
    )
  }
)

const ParagraphQuestionView = ({ onChange, data, layout }) => {
  const {
    setUseAutocomplete,
    setBlockFunctions,
    preventChanges,
    getBlurProps
  } = useBlockCompletion()
  const { forceQuestionSubmit } = useBlocksConfig()
  const answerRef = useRef(data.answer)

  useEffect(() => {
    answerRef.current = data.answer
  }, [data.answer])

  useEffect(() => {
    // Autocomplete only when there is no feedback AND the host has not
    // forced explicit submit (legacy assessments always use Submit).
    if (!forceQuestionSubmit && (!data.feedback || !data.feedback.length)) {
      setUseAutocomplete(true)
    }
  }, [forceQuestionSubmit, data.feedback])

  useEffect(() => {
    setBlockFunctions({
      validateInput,
      getBlockIsCompleted: (nextData) => isAnswerComplete(nextData, layout)
    })
  }, [])

  const validateInput = (data) => {
    const answer = data.answer
    const input = getTextFromHTML(getAnswerText(answer))

    if (!input) return BLOCK_INPUT_STATES.NONE

    const isValidInput = input && input.length >= MIN_ANSWER_LENGTH
    return isValidInput ? BLOCK_INPUT_STATES.VALID : BLOCK_INPUT_STATES.INVALID
  }

  // Handle edge/legacy answer text cases
  const getAnswerText = (answer) => {
    return answer && (!!answer.answerText || !!answer.answerHTML)
      ? answer.answerText
        ? answer.answerText // answerText is legacy plain text
        : answer.answerHTML === "undefined"
        ? ""
        : answer.answerHTML
      : ""
  }

  const handleChange = useCallback(
    (text) => {
      const currentAnswer = answerRef.current
      const answerChange = currentAnswer?.answerText
        ? { answerText: text }
        : { answerHTML: text }

      const change = {
        answer: {
          ...currentAnswer,
          ...answerChange
        }
      }

      onChange(change, true)
    },
    [onChange]
  )

  return (
    <ParagraphQuestionViewWrapper>
      <div {...getBlurProps()}>
        {data.answer && !!data.answer.answerText ? (
          <TextArea
            className="blocks-paragraph__textarea"
            minRows={3}
            controlled={false}
            autocorrect="on"
            value={getAnswerText(data.answer)}
            onChange={handleChange}
            disabled={isFirefox() ? null : preventChanges}
            readOnly={isFirefox() ? preventChanges : null}
          />
        ) : (
          <ParagraphRichTextEditor
            html={getAnswerText(data.answer)}
            onChange={handleChange}
            preventChanges={preventChanges}
          />
        )}
      </div>
    </ParagraphQuestionViewWrapper>
  )
}

export default ParagraphQuestionView
